import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Box,
  Typography,
  Chip,
  Icon,
  useTheme,
  alpha,
} from "@mui/material";
import React from "react";
import { formatCurrency } from "../../utils/formatters";
import { DayBookEmptyState } from "./daybook.emptystate";
import { formatOrderId, formatTransactionId } from "./utils/id-formatter";
import {
  getPaymentMethodColor,
  getPaymentMethodIcon,
} from "./utils/payment-methods-formatter";
import { DaybookTransaction, PanelProps } from "./types";
import { getPanelStyles } from "./styles";

const PaymentMethodChip: React.FC<{ method: string }> = ({ method }) => {
  const theme = useTheme();

  const formatPaymentMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "mastercard":
        return "CARD";
      case "benefitpay":
        return "MOBILE";
      default:
        return method.toUpperCase();
    }
  };

  return (
    <Chip
      icon={<Icon fontSize="small" sx={{ fontSize: 16 }}>{getPaymentMethodIcon(method)}</Icon>}
      label={formatPaymentMethod(method)}
      color={getPaymentMethodColor(method)}
      size="small"
      variant="outlined"
      sx={{
        height: 24,
        textTransform: "uppercase",
        fontWeight: 500,
        '&.MuiChip-colorSuccess': {
          color: '#1E8078',
          borderColor: alpha('#1E8078', 0.3),
          backgroundColor: alpha('#1E8078', 0.05),
        },
        '&.MuiChip-colorWarning': {
          color: theme.palette.warning.main,
          borderColor: alpha(theme.palette.warning.main, 0.3),
          backgroundColor: alpha(theme.palette.warning.main, 0.05),
        }
      }}
    />
  );
};

const LoadingSkeleton: React.FC = () => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Transaction ID</TableCell>
          <TableCell>Order ID</TableCell>
          <TableCell>Date</TableCell>
          <TableCell align="right">Amount</TableCell>
          <TableCell>Payment Method</TableCell>
          <TableCell>Reference</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[1, 2, 3, 4, 5].map((i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={150} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rounded" width={100} height={24} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={100} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyState: React.FC = () => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Transaction ID</TableCell>
          <TableCell>Order ID</TableCell>
          <TableCell>Date</TableCell>
          <TableCell align="right">Amount</TableCell>
          <TableCell>Payment Method</TableCell>
          <TableCell>Reference</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell colSpan={6} sx={{ border: "none", p: 0 }}>
            <DayBookEmptyState
              icon="payments"
              title="No Transactions Found"
              message="This session has no transactions yet. Transactions will appear here once payments are processed."
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

const TransactionRow: React.FC<{ transaction: DaybookTransaction }> = ({
  transaction,
}) => {
  const theme = useTheme();
  return (
    <TableRow 
      sx={{ 
        '&:hover': { 
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          cursor: 'pointer'
        },
        transition: 'background-color 0.2s ease'
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
          {formatTransactionId(transaction.id)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatOrderId(transaction.order_id)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {new Date(transaction.entry_date).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
          {formatCurrency(transaction.total_amount)}
        </Typography>
      </TableCell>
      <TableCell>
        <PaymentMethodChip method={transaction.payment_method} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {transaction.reference || "-"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const DayBookTransactionsPanel: React.FC<PanelProps> = ({
  loading,
  data,
}) => {
  const theme = useTheme();
  const styles = getPanelStyles(theme);

  if (loading) return <LoadingSkeleton />;
  if (data.length === 0) return <EmptyState />;

  return (
    <Box sx={styles.container}>
      <TableContainer sx={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Transaction ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Order ID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Payment Method</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((transaction: DaybookTransaction) => (
              <TransactionRow key={transaction.id} transaction={transaction} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
